import { FC, Fragment } from 'react'
import { useUA } from 'context'
import gql from 'graphql-tag'
import queryString from 'query-string'
import styled from 'styled-components'
import {
  CoverLetterListContent_CurrentUserFragment,
  CoverLetterListContent_CoverLetterElasticEdgeFragment,
} from 'generated/graphql'
import { LinkProps } from 'components/Link'
import { filterEmptyField } from 'utils/utils'
import NotFound from '../NotFound'
import CoverLetterListItemDesktop from '../desktop/components/CoverLetterList/CoverLetterListItemDesktop'
import LoadingItemListDesktop from '../desktop/components/LoadingItemList'
import CoverLetterListItemMobile from '../mobile/components/CoverLetterList/CoverLetterListItemMobile'
import LoadingItemListMobile from '../mobile/components/LoadingItemList'
import { useSearchStateContext, TabState } from '../useSearchState'

export const getQueryParams = (context: any) => {
  const { search, page, orderBy } = context
  return queryString.stringify(
    filterEmptyField({
      tab: TabState.ALL,
      ...search,
      page,
      sort: orderBy?.orderField,
    }),
  )
}

export const getCoverLetterItemLinkProps = (
  context: any,
  coverLetterID?: string,
) => {
  const queryParams = getQueryParams(context)

  return {
    href: coverLetterID ? `/cover-letter/[id]?${queryParams}` : '',
    as: coverLetterID ? `/cover-letter/${coverLetterID}?${queryParams}` : '',
    naked: true,
  } as LinkProps
}

interface CoverLetterListContentProps {
  currentUser?: CoverLetterListContent_CurrentUserFragment | null
  coverLetterEdges: CoverLetterListContent_CoverLetterElasticEdgeFragment[]
  loading: boolean
}

const CoverLetterListContent: FC<CoverLetterListContentProps> = ({
  currentUser,
  coverLetterEdges,
  loading,
}) => {
  const { isMobile } = useUA()
  const context = useSearchStateContext()

  if (loading) {
    return isMobile ? <LoadingItemListMobile /> : <LoadingItemListDesktop />
  }

  if (!coverLetterEdges.length) {
    return <NotFound />
  }
  return (
    <>
      {coverLetterEdges.map((edge, index) => {
        const coverLetter = edge.node
        const LinkProps = getCoverLetterItemLinkProps(context, coverLetter?.id)

        return (
          <Fragment key={index}>
            {isMobile ? (
              <CoverLetterListItemMobile
                coverLetter={coverLetter}
                coverLetterHighlight={edge.highlight}
                currentUser={currentUser}
                LinkProps={LinkProps}
              />
            ) : (
              <CoverLetterListItemDesktop
                coverLetter={coverLetter}
                coverLetterHighlight={edge.highlight}
                currentUser={currentUser}
                LinkProps={LinkProps}
              />
            )}
            <StyledWrapper>
              <hr className="divider" />
            </StyledWrapper>
          </Fragment>
        )
      })}
    </>
  )
}

export default CoverLetterListContent

const StyledWrapper = styled.div`
  padding-right: 10px;
  .divider {
    margin: 0;
    border: 0.5px solid #eee;
  }
`

gql`
  fragment CoverLetterListContent_currentUser on User {
    ...CoverLetterListItem_currentUser
  }
  fragment CoverLetterListContent_coverLetterElasticEdge on CoverLetterElasticEdge {
    node {
      id
      ...CoverLetterListItem_coverLetter
    }
    highlight
  }
`
